import {
  createStyles,
  Link,
  Tooltip,
  Typography,
  withStyles,
  type WithStyles,
} from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import { useTranslations } from "adapters/i18n";
import { Show } from "features/shared/components";
import React, { useMemo } from "react";
import type {
  EnrichedAnimalCategory,
  LivestockInfoFromFarm,
} from "shared/types";

type ProjectedWeightColumnCellProps = {
  enrichedAnimalCategories: EnrichedAnimalCategory[];
  onOpenWeightProjectionDetails: (row: LivestockInfoFromFarm) => void;
  row: LivestockInfoFromFarm;
  value: string | null;
} & WithStyles<typeof styles>;

function ProjectedWeightColumnCell({
  classes,
  enrichedAnimalCategories,
  onOpenWeightProjectionDetails,
  row,
  value,
}: ProjectedWeightColumnCellProps) {
  const { t } = useTranslations();

  const categoriesMap = new Map(
    enrichedAnimalCategories.map((cat) => [cat.id, cat]),
  );

  const category = row.animalCategoryId
    ? categoriesMap.get(row.animalCategoryId)
    : null;

  const weightGainCurve = category?.weightGainCurve ?? null;

  const infoMessage = useMemo(() => {
    if (
      !weightGainCurve &&
      !row.measuredWeight?.weight &&
      !row.measuredWeight?.weightDate
    ) {
      return t("livestock:projected-weight-disclaimer.no-data");
    }

    if (
      !weightGainCurve &&
      !row.measuredWeight?.weight &&
      row.measuredWeight?.weightDate
    ) {
      return t(
        "livestock:projected-weight-disclaimer.no-weight-gain-curve-and-measured-weight",
      );
    }

    if (
      !weightGainCurve &&
      row.measuredWeight?.weight &&
      !row.measuredWeight?.weightDate
    ) {
      return t(
        "livestock:projected-weight-disclaimer.no-weight-gain-curve-and-measured-weight-date",
      );
    }

    if (
      !weightGainCurve &&
      row.measuredWeight?.weight &&
      row.measuredWeight?.weightDate
    ) {
      return t("livestock:projected-weight-disclaimer.no-weight-gain-curve");
    }

    if (
      weightGainCurve &&
      !row.measuredWeight?.weight &&
      !row.measuredWeight?.weightDate
    ) {
      return t(
        "livestock:projected-weight-disclaimer.no-measured-weight-and-date",
      );
    }

    if (
      weightGainCurve &&
      !row.measuredWeight?.weight &&
      row.measuredWeight?.weightDate
    ) {
      return t("livestock:projected-weight-disclaimer.no-measured-weight");
    }

    if (
      weightGainCurve &&
      row.measuredWeight?.weight &&
      !row.measuredWeight?.weightDate
    ) {
      return t("livestock:projected-weight-disclaimer.no-measured-weight-date");
    }

    return null;
  }, [
    row.measuredWeight?.weight,
    row.measuredWeight?.weightDate,
    t,
    weightGainCurve,
  ]);

  const hasProjectedWeight =
    row.projectedWeight != null && row.projectedWeight.weight !== 0;
  const hasMeasuredWeight =
    row.measuredWeight != null && row.measuredWeight.weight !== 0;

  // Consider the measured weight as more up-to-date than the projected weight only if it was created after the projected weight.
  const showDefaultContent =
    hasMeasuredWeight &&
    (hasProjectedWeight || (value !== null && value !== undefined));

  const handleSeeMore = () => {
    onOpenWeightProjectionDetails(row);
  };

  return (
    <div className={classes.root}>
      <Show
        when={showDefaultContent}
        fallback={
          <NoProjectedWeightContent
            infoMessage={infoMessage}
            classes={classes}
          />
        }
      >
        <Typography
          className={classes.projectedWeightText}
          component="span"
          data-testid="lot-projected-weight-value"
        >
          {t("common:measure.x-kg", { value })}
        </Typography>
        <Link
          aria-disabled={!hasProjectedWeight}
          component={"button" as never}
          data-testid="see-more-projected-weight-link"
          onClick={handleSeeMore}
          style={{
            textDecoration: !hasProjectedWeight ? "line-through" : undefined,
          }}
        >
          {t("common:actions.see-more")}
        </Link>
      </Show>
    </div>
  );
}

type NoProjectedWeightContentProps = {
  infoMessage: string | null;
} & WithStyles<typeof styles>;

function NoProjectedWeightContent({
  infoMessage,
  classes,
}: NoProjectedWeightContentProps) {
  const { t } = useTranslations();

  return (
    <div
      className={classes.noProjectedWeightContainer}
      data-testid="no-projected-weight-content"
    >
      <Typography className={classes.projectedWeightText}>
        {t("common:not-available")}
      </Typography>
      {infoMessage && (
        <Tooltip
          title={infoMessage}
          classes={{ tooltip: classes.noProjectedWeightTooltip }}
        >
          <InfoIcon
            className={classes.noProjectedWeightIcon}
            data-testid="tooltip-trigger"
          />
        </Tooltip>
      )}
    </div>
  );
}

const styles = () =>
  createStyles({
    root: {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      maxWidth: "200px",
    },
    noProjectedWeightContainer: {
      alignItems: "center",
      color: "#757575",
      display: "flex",
    },
    projectedWeightText: {
      textTransform: "capitalize",
      "@media print": {
        display: "block",
        fontSize: "inherit",
      },
    },
    noProjectedWeightIcon: {
      cursor: "pointer",
      marginLeft: 12,
      fontSize: "16px",
      "@media print": {
        display: "none",
      },
    },
    noProjectedWeightTooltip: {
      maxWidth: 200,
    },
  });

export default withStyles(styles)(ProjectedWeightColumnCell);
